import React from 'react';
import TransactionEditor from 'views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import { Transaction, TransactionWrapper } from 'types/rest/assetCatalogTypes';
import { WizardTransactionListState } from 'store/transactions/transactionsTypes';
import { WizardTransactionTypes } from 'components/modal/wizard/WizardTransactionTypes';
import womansvg from 'assets/img/woman.svg';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import i18n from 'i18n';
import _ from 'lodash';
import { confirm } from 'components/ui/modal/BlazeConfirmationModal';
import ExpandLink from 'components/blazeTable/ExpandLink';
import clsx from 'clsx';
import styles from 'components/modal/WizardModal.module.css';
import BzmIcon from '../../icons/BzmIcon';

interface WizardTransactionTableProps {
  transactions: WizardTransactionListState;
  serviceId: number;
  wizardTransactionActions: (action: WizardTransactionTypes, payload: any) => void;
}

const WizardTransactionTable = (props: WizardTransactionTableProps) => {
  const expandTransaction = (txId: number | null) => {
    const transaction = _.find(props.transactions.transactions, tx => tx.id === txId);
    const expandId = txId === props.transactions.expandedTransactionId ? null : (txId as number);
    props.wizardTransactionActions('expandTransactionRow', {
      expandId: expandId,
      txName: transaction ? transaction.name : '',
    });
  };

  const onTransactionSave = (transactionWrapper: TransactionWrapper, isCreate: boolean) => {
    if (isCreate) {
      props.wizardTransactionActions('save', transactionWrapper);
    } else {
      props.wizardTransactionActions('update', transactionWrapper);
    }
  };

  const expandItem = (rowData: Transaction) => {
    return (
      <TransactionEditor
        transaction={rowData}
        serviceId={props.serviceId}
        onSave={onTransactionSave}
        isCreate={props.transactions.isCreatingNewTransaction && rowData.id === -1}
        isReadOnly={false}
        isWizardModal={true}
        setInvalidTransaction={() => props.wizardTransactionActions('invalidTx', null)}
      />
    );
  };

  const emptyListMessage = (
    <div className={'text-center'}>
      <h2 className="text-center">{i18n.t('wizard:details.behavior.noTransactionDefined')}</h2>
      <p className={'mb-5'}>{i18n.t('wizard:details.behavior.addFirstTransaction')} </p>
      <img src={womansvg} alt={'woman'} className={'img-fluid mb-4'} />
    </div>
  );

  const deleteRowClicked = (transaction: Transaction) => {
    if (transaction.id === -1) {
      props.wizardTransactionActions('deleteTx', transaction);
    } else {
      const modalBody = (
        <div>
          {i18n.t('transactions:table.inlineDeleteWarn')} <strong className="text-break">{transaction.name}</strong>?
        </div>
      );
      confirm({
        confirmation: modalBody,
        yesOptionTitle: i18n.t('common:actions.delete'),
        title: i18n.t('transactions:table.deleteTxn'),
      })
        .then(() => {
          props.wizardTransactionActions('deleteTx', transaction);
        })
        .catch(() => {
          // User chose "No"
        });
    }
  };

  const getColumnValue = (value: any, txId: number | undefined): any => {
    if (props.transactions.isCreatingNewTransaction && txId === -1) {
      return '-';
    }
    return value;
  };

  // Definition of table columns
  const colsDefinition: BlazeTableColDef[] = [
    {
      header: i18n.t('transactions:table.txnName'),
      width: 250,
      accessor: 'name',
      cellComponent: (rowData: Transaction) => {
        return getColumnValue(
          <ExpandLink
            value={rowData.name}
            id={rowData.id as number}
            title={rowData.name}
            onToggleExpandDetail={() => expandTransaction(rowData.id as number)}
          />,
          rowData.id,
        );
      },
    },
    {
      header: i18n.t('transactions:table.method'),
      width: 100,
      accessor: 'method',
      cellComponent: (rowData: Transaction) => {
        return getColumnValue(_.get(rowData, 'dsl.requestDsl.method'), rowData.id);
      },
    },
    {
      header: i18n.t('transactions:table.requestUrl'),
      width: 200,
      accessor: 'requestUrl',
      cellComponent: (rowData: Transaction) => {
        return getColumnValue(_.get(rowData, 'dsl.requestDsl.url.matchingValue'), rowData.id);
      },
    },
    {
      header: i18n.t('transactions:table.responseStatus'),
      accessor: 'responseStatus',
      cellComponent: (rowData: Transaction) => {
        return getColumnValue(_.get(rowData, 'dsl.responseDsl.status'), rowData.id);
      },
    },
    {
      header: i18n.t('actions.actions'),
      width: 100,
      accessor: 'actions',
      cellComponent: (rowData: Transaction) => {
        return (
          <button
            disabled={false}
            id={`qa-tbl-row-delete-transaction-${rowData.id}`}
            className="btn btn-link btn-no-hover"
            title={i18n.t('transactions:table.deleteTxn')}
            onClick={() => deleteRowClicked(rowData)}
          >
            <BzmIcon variant={'icon-bzm-delete'} className={clsx(styles.wizardIcons, 'icon-xs')} />
            <span className="sr-only">${i18n.t('transactions:table.deleteTxn')}</span>
          </button>
        );
      },
    },
  ];

  const tableOptions: BlazeTableOptions = {
    expandDetailComponent: (rowData: any) => expandItem(rowData),
    showRowExpander: true,
    columnDef: colsDefinition,
    pageable: true,
    selectable: false,
    pageSize: 10,
    minHeight: 338,
    tableName: 'transactionList',
    noResultsText: emptyListMessage,
    customHeaderClassName: 'flex-table-item-top',
    customTableClassName: 'flex-table-condensed',
  };

  return (
    <div style={{ paddingBottom: '40px' }}>
      <BlazeTable
        onExpandedItemChange={expandTransaction}
        expandedItem={props.transactions.expandedTransactionId}
        options={tableOptions}
        data={props.transactions.transactions}
        showPageSizeDropdown={false}
      />
    </div>
  );
};

export default WizardTransactionTable;
